import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { FileData } from "../@types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs));
}

export const formatCurrency = (amount: number): string => {
  if (!amount) return "0";
  return `₦${amount.toLocaleString()}`;
};

export const formatCurrencyForTable = (amount: number): string => {
  if (!amount) return "0";
  return `${amount.toLocaleString()}`;
};

export function formatSignedNumber(value: number, locale = "en-US"): string {
  if (value < 0) {
    return value.toLocaleString(locale);
  }

  return `+${value.toLocaleString(locale)}`;
}

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }

  const parts = dateString.split("-");
  if (parts.length !== 3) {
    return "";
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return "";
  }

  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return "";
  }

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  };

  const formattedDate = date.toLocaleDateString("en-US", options);
  const formattedParts = formattedDate.split(" ");

  if (formattedParts.length === 3) {
    return `${formattedParts[1].replace(",", "")}, ${formattedParts[0]}, ${
      formattedParts[2]
    }`;
  }

  return "";
}

export function truncate(str?: string, maxLength: number = 23): string {
  if (!str) return "";
  return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
}

// Convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result as string;
      // Remove the data:image/jpeg;base64, part
      const base64Data = base64String.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const fileToCanvas = (file: File): Promise<HTMLCanvasElement> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("Canvas context not available");

        ctx.drawImage(img, 0, 0);
        resolve(canvas);
      };

      img.onerror = (err) => reject(err);
      img.src = reader.result as string;
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

// Get file extension
const getFileExtension = (filename: string): string => {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  return ext ? `.${ext}` : "";
};

export async function processFile(
  file: File | undefined,
  fieldName: string
): Promise<FileData | null> {
  if (file && file instanceof File) {
    try {
      const base64Data = await fileToBase64(file);
      return {
        name: file.name,
        extension: getFileExtension(file.name),
        data: base64Data,
      };
    } catch (error) {
      console.error(`Error converting ${fieldName} file to base64:`, error);
      throw new Error("Failed to process uploaded file");
    }
  }
  return null;
}

export function docFormatDate(dateString: string): string {
  const date = new Date(dateString);

  const day = date.getDate();
  const year = date.getFullYear();

  const month = date.toLocaleString("en-US", { month: "long" });

  return `${day} of ${month}, ${year}`;
}

export function countDocuments(a: string, b: string, c: string): number {
  if (!a || !b || !c) return 0;
  return [a, b, c].filter((str) => str.trim() !== "").length;
}

export function formatDateDDMMYYYY(dateTime: string): string {
  if (!dateTime) return "";
  const date = new Date(dateTime);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

const apiBase = process.env.REACT_APP_FLEET_PRO_APP_API_URL;

export const buildApiUrl = (path: string): string => {
  if (!apiBase) throw new Error("API base URL is not defined");
  if (!path || path === "") return "";
  return `${apiBase.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
};

export const getStatusInfo = ({
  value,
  item,
}: {
  value: boolean;
  item: string;
}) => {
  if (!value || item === "Declined")
    return {
      label: "Declined",
      color:
        "flex items-center justify-center w-[75px] bg-red-100 text-primary border border-primary",
    };
  if (item === "Approved")
    return {
      label: "Approved",
      color:
        "flex items-center justify-center w-[75px] bg-green-100 text-[#059348] border border-[#059348]",
    };
  return {
    label: "Pending",
    color:
      "flex items-center justify-center w-[75px] bg-yellow-100 text-[#E8BB00] border border-[#E8BB00]",
  };
};

export function removeAfterDash(text?: string): string {
  if (!text) return "";

  // Split at "--" and return the trimmed left part
  return text.split("--")[0].trim();
}
